import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import Button, {ButtonVariant} from 'components/Button';
import { FaChevronRight, FaChevronLeft, FaCalendarDay } from 'react-icons/fa';
import CustomDatePicker from 'components/DatePicker/CustomDatePicker';

interface DateBarProps {
  date: Date;
  onDateChange: (newDate: Date) => void;
}

const DateBar = (props: DateBarProps) => {
  const [showPicker, setShowPicker] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() === today.getDate()
    );
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const onDateChange = (direction: "next"|"previous") => {
    const newDate = new Date(props.date);
    if (direction == "next") {
      newDate.setDate(newDate.getDate() + 1);
      props.onDateChange(newDate);
    } else {
      newDate.setDate(newDate.getDate() - 1);
      props.onDateChange(newDate);
    }
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showPicker &&
        buttonRef.current &&
        pickerRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPicker]);

  return (
   <div className="flex items-center justify-between rounded-lg p-2 text-palette-4 min-w-max">
     <div className="flex justify-center items-center space-x-4 flex-grow rounded-lg p-2 text-palette-4">
       <Button
         variant={ButtonVariant.FlatNoFill}
         icon={<FaChevronLeft />}
         onClick={() => onDateChange("next")}
         disabled={isToday(props.date)}
         size="md"
       />
       <div className="w-fit whitespace-nowrap"> {formatDate(props.date)} </div>
     <div ref={buttonRef}>
       <Button
         variant={ButtonVariant.FlatNoFill}
         icon={<FaCalendarDay />}
         onClick={() => setShowPicker(!showPicker)}
         size="md"
       />
     </div>
       <Button
         variant={ButtonVariant.FlatNoFill}
         icon={<FaChevronRight />}
         onClick={() => onDateChange("previous")}
         size="md"
       />
     </div>
     {showPicker && buttonRef.current &&
       ReactDOM.createPortal(
         <div
           ref={pickerRef}
           style={{
             position: 'absolute',
             top: buttonRef.current.getBoundingClientRect().bottom + window.scrollY,
             left: buttonRef.current.getBoundingClientRect().left + window.scrollX + buttonRef.current.clientWidth,
           }}
           className="z-50 bg-white p-2 rounded shadow"
         >
           <CustomDatePicker
             selected={props.date}
             initialDate={props.date}
             onChange={(date: Date) => {
               props.onDateChange(date);
               setShowPicker(false);
             }}
           />
         </div>,
         document.body
       )
     }
    </div>
  );
};

export default DateBar;
